import {
  DangerAlert,
  LessonListItem,
  MultiSkeleton,
  PageContainer,
  PageTitle,
} from '@/components'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import {
  getGradesListGradePostOptions,
  getLessonsListGradeSubjectLessonsPostOptions,
  getSubjectsListGradeSubjectPostOptions,
} from '@/client/@tanstack/react-query.gen'

import { useAuthHeaders } from '@/providers'
import { useDebouncedCallback } from 'use-debounce'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

const lessonPlanSchema = z.object({
  grade: z.string().optional(),
  subject: z.string().optional(),
})

type LessonPlanForm = z.infer<typeof lessonPlanSchema>

export const Route = createFileRoute(
  '/_authRoutes/_appWrapper/lesson-planning',
)({
  component: RouteComponent,
  validateSearch: (search) => lessonPlanSchema.parse(search),
})

function RouteComponent() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { grade = '', subject: subject = '' } = useSearch({
    from: '/_authRoutes/_appWrapper/lesson-planning',
  })
  const headers = useAuthHeaders()

  const form = useForm<LessonPlanForm>({
    resolver: zodResolver(lessonPlanSchema),
    defaultValues: {
      grade,
      subject,
    },
  })

  const { data: grades = [], error: gradesError } = useQuery({
    ...getGradesListGradePostOptions({ headers }),
  })

  const { data: subjects = [], error: subjectsError } = useQuery({
    ...getSubjectsListGradeSubjectPostOptions({ path: { grade }, headers }),
    enabled: !!grade,
  })

  const {
    data: lessons,
    isLoading,
    error: lessonsError,
  } = useQuery({
    ...getLessonsListGradeSubjectLessonsPostOptions({
      path: { grade, subject },
      headers,
    }),
    enabled: !!grade && !!subject,
  })

  const { watch, handleSubmit } = form

  const onSubmit = (data: LessonPlanForm) => {
    // Navigate to the same route with search params
    navigate({
      to: '.',
      search: (prev) => ({
        ...prev,
        grade: data.grade,
        subject: data.subject,
      }),
      replace: true,
    })
  }

  const debouncedOnSubmit = useDebouncedCallback(onSubmit, 200)

  useEffect(() => {
    const subscription = watch(() => handleSubmit(debouncedOnSubmit)())
    return () => subscription.unsubscribe()
  }, [debouncedOnSubmit, handleSubmit, watch])

  const error = gradesError || subjectsError || lessonsError
  return (
    <PageContainer>
      <div className="space-y-6">
        <PageTitle
          title={t('lessonPlanning.title')}
          subtitle={t('lessonPlanning.subtitle')}
        />

        {/* <Card>
          <CardContent> */}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="grid flex-row gap-4  items-end sm:grid-cols-2">
              <FormField
                control={form.control}
                name="grade"
                render={({ field }) => (
                  <FormItem className="flex-grow-1">
                    <FormLabel>{t('lessonPlanning.grade')}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full ">
                          <SelectValue
                            placeholder={t('lessonPlanning.selectGrade')}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {/* eslint-disable-next-line
                        @typescript-eslint/no-unnecessary-condition */}
                        {grades?.map(({ grade: gradeString = '' }) => (
                          <SelectItem key={gradeString} value={gradeString}>
                            {t('lessonPlanning.gradeNumber', {
                              number: gradeString.replace('year', ''),
                            })}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="subject"
                render={({ field }) => (
                  <FormItem className="flex-grow-1">
                    <FormLabel>{t('lessonPlanning.subject')}</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      disabled={!grade}
                    >
                      <FormControl>
                        <SelectTrigger className="w-full ">
                          <SelectValue
                            placeholder={t('lessonPlanning.selectSubject')}
                          />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {/* eslint-disable-next-line
                        @typescript-eslint/no-unnecessary-condition */}
                        {subjects?.map(({ subject: subjectString = '' }) => (
                          <SelectItem key={subjectString} value={subjectString}>
                            {t(`lessonPlanning.subjects.${subjectString}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
        {error && (
          <DangerAlert>
            <div className="text-lg">{t('errorBoundary.title')}</div>
            <p className="text-sm">{t('errorBoundary.message')}</p>
          </DangerAlert>
        )}
        {/* </CardContent>
        </Card> */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(300px,1fr))] lg:grid-cols-[repeat(auto-fill,minmax(400px,1fr))] gap-4">
          {isLoading ? (
            <MultiSkeleton
              count={8}
              className="aspect-[10/9.8] lg:aspect-[10/9.2]"
            />
          ) : (
            lessons?.map((lesson) => (
              <LessonListItem key={lesson.id} {...lesson} />
            ))
          )}
        </div>
      </div>
    </PageContainer>
  )
}
